/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import type Amount from 'linced-app/models/finance/daily-summary/amount';
import type Banked from 'linced-app/models/finance/daily-summary/banked';
import type InSafe from 'linced-app/models/finance/daily-summary/in-safe';
import type DepositSlip from 'linced-app/models/finance/deposit-slip';
import type Person from 'linced-app/models/person';
import type School from 'linced-app/models/school';

// Proto: finance.DailySummary.
export default class DailySummary extends Model {
  declare [Type]: 'finance/daily-summary';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @attr('string')
  declare reportDate: string | undefined;

  @belongsTo('person', { async: true, inverse: null })
  declare signedById: AsyncBelongsTo<Person>;

  @alias('signedById')
  declare signedBy: AsyncBelongsTo<Person>;

  @attr('number', { defaultValue: 0 })
  declare signedAt: number;

  @computed('signedAt')
  get isSigned() {
    return Boolean(this.signedAt);
  }

  @belongsTo('person', { async: true, inverse: null })
  declare feesReportSignedById: AsyncBelongsTo<Person>;

  @alias('feesReportSignedById')
  declare feesReportSignedBy: AsyncBelongsTo<Person>;

  @attr('number', { defaultValue: 0 })
  declare feesReportSignedAt: number;

  @computed('feesReportSignedAt')
  get feesReportSigned() {
    return Boolean(this.feesReportSignedAt);
  }

  @belongsTo('person', { async: true, inverse: null })
  declare refundsReportSignedById: AsyncBelongsTo<Person>;

  @alias('refundsReportSignedById')
  declare refundsReportSignedBy: AsyncBelongsTo<Person>;

  @attr('number', { defaultValue: 0 })
  declare refundsReportSignedAt: number;

  @computed('refundsReportSignedAt')
  get refundsReportSigned() {
    return Boolean(this.refundsReportSignedAt);
  }

  @fragment('finance/daily-summary/banked')
  declare banked: Banked | null;

  @fragment('finance/daily-summary/in-safe')
  declare inSafe: InSafe | null;

  @hasMany('finance/deposit-slip', { async: true, inverse: 'dailySummaryId' })
  declare depositSlips: AsyncHasMany<DepositSlip>;

  @fragmentArray('finance/daily-summary/amount')
  declare methodTotals: FragmentArray<Amount>;

  @computed('depositSlips.@each.cashDiscrepanciesTotal')
  get cashDiscrepanciesTotal() {
    if (isEmpty(this.depositSlips)) {
      return 0;
    }
    return resolved(this.depositSlips).reduce((total, slip) => {
      return total + slip.cashDiscrepanciesTotal;
    }, 0);
  }

  @computed('depositSlips.@each.chequeDiscrepanciesTotal')
  get chequeDiscrepanciesTotal() {
    if (isEmpty(this.depositSlips)) {
      return 0;
    }
    return resolved(this.depositSlips).reduce((total, slip) => {
      return total + slip.chequeDiscrepanciesTotal;
    }, 0);
  }

  @computed('depositSlips.@each.moneyOrderDiscrepanciesTotal')
  get moneyOrderDiscrepanciesTotal() {
    if (isEmpty(this.depositSlips)) {
      return 0;
    }
    return resolved(this.depositSlips).reduce((total, slip) => {
      return total + slip.moneyOrderDiscrepanciesTotal;
    }, 0);
  }
}
